/**
 * 门店时钟：后端判定附加费用的是**服务器时钟**换算到**门店时区**（`Tool::calDatetime(DEFAULT_GMT, <门店时区>, date(...))`），
 * 不是顾客手机的时钟。手机时间不准（手动改过、没联网校时）时，前端按手机时间判出来的时段 / 星期就会和后端不一样，
 * 建单被 `##9##` 拒。
 *
 * 做法：每个接口响应的信封里都有 `serverTime`（'Y-m-d H:i:s'）+ `serverTimeZone`（'Australia/Melbourne'，
 * `ApiCommonController::afterAction`），收到时记下「服务器时间 − 手机时间」的偏移，之后用 `serverNowMs()` 取「现在」。
 * 偏移只用于附加费判定，别的页面逻辑照旧。
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { WEEKDAYS } from '@/utils/weekdays'

dayjs.extend(utc)
dayjs.extend(timezone)

/** 后端 `Venue::DEFAULT_GMT` / `getTimezone` 的默认值 */
export const DEFAULT_VENUE_TIMEZONE = 'Australia/Melbourne'

/**
 * 一次请求从发出到收到响应超过这个时长，就不用它的 serverTime 校时（review 第 1 轮 user-legacy #3）。
 * serverTime 是服务器处理请求那一刻的时间，而偏移是拿「收到响应时的手机时间」去减的：标签页被冻结 / 切到后台、
 * 网络卡住时，响应回调可能在服务器盖戳之后很久才跑，算出来的「现在」会被拨慢这么久（跨过时段边界就会判错费率）。
 * 正常往返远小于 3 秒；serverTime 本身只精确到秒。
 */
export const MAX_SERVER_TIME_ROUND_TRIP_MS = 3000

const SERVER_TIME_SHAPE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/

let serverOffsetMs = 0

/**
 * 门店时区，前端只在这一处解析（review 第 1 轮 #11：App.vue 与 requestMixin 以前各写一套、优先级不同）。
 * 后端 `Venue::getTimezone` 读 `options.timezone`，空就用墨尔本；get-venue 的 `timeZone` 就是它算出来的，作为后备。
 * @param {object} venue get-venue 的 data
 * @returns {string}
 */
export function venueTimeZoneOf(venue) {
  return venue?.settings?.options?.timezone || venue?.timeZone || DEFAULT_VENUE_TIMEZONE
}

function inZone(epochMs, timeZone) {
  try {
    const d = dayjs(epochMs).tz(timeZone || DEFAULT_VENUE_TIMEZONE)
    if (d.isValid()) return d
  } catch (e) {
    // 时区名写错时 Intl 会抛 RangeError，按后端默认时区算
  }
  return dayjs(epochMs).tz(DEFAULT_VENUE_TIMEZONE)
}

/**
 * 某一时刻在门店时区里的日期 / 时间 / 星期，对应后端 getCurrentSurcharge 里的 `$todayArr` 和 `date('w', …)`。
 * @param {number} epochMs
 * @param {string} timeZone 门店时区（venueTimeZoneOf）
 * @returns {{ date: string, time: string, weekDay: string }}
 */
export function venueNowParts(epochMs, timeZone) {
  const d = inZone(epochMs, timeZone)
  const [date, time] = d.format('YYYY-MM-DD HH:mm:ss').split(' ')
  return { date, time, weekDay: WEEKDAYS[d.day()] }
}

/**
 * 记下接口信封里的服务器时间。
 * @param {string} serverTime 'Y-m-d H:i:s'
 * @param {string} serverTimeZone 例如 'Australia/Melbourne'
 * @param {number} [receivedAtMs] 收到响应时的手机时间
 * @param {number} [sentAtMs] 发出请求时的手机时间（requestMixin 记在 config.metadata.requestStartedAt）；
 *   往返超过 MAX_SERVER_TIME_ROUND_TRIP_MS 的样本丢掉。没有时照旧采用（兼容没经过请求拦截器的调用）
 * @returns {boolean} 这次有没有更新偏移
 */
export function noteServerTime(serverTime, serverTimeZone, receivedAtMs = Date.now(), sentAtMs) {
  if (typeof serverTime !== 'string' || !SERVER_TIME_SHAPE.test(serverTime)) return false
  if (Number.isFinite(sentAtMs) && Number.isFinite(receivedAtMs) && receivedAtMs - sentAtMs > MAX_SERVER_TIME_ROUND_TRIP_MS) return false
  let serverMs
  try {
    serverMs = dayjs.tz(serverTime, serverTimeZone || DEFAULT_VENUE_TIMEZONE).valueOf()
  } catch (e) {
    return false
  }
  if (!Number.isFinite(serverMs) || !Number.isFinite(receivedAtMs)) return false
  serverOffsetMs = serverMs - receivedAtMs
  return true
}

/** 按服务器时钟的「现在」（毫秒）。还没收到过 serverTime 时就是手机时间。 */
export function serverNowMs() {
  return Date.now() + serverOffsetMs
}

/** 测试用：清掉偏移 */
export function __resetServerClock() {
  serverOffsetMs = 0
}
